import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Col,
  Row,
  Input,
  Card,
  FormGroup,
  Spinner,
} from "reactstrap";
import {
  capitalizeFirstLetter,
  formatCentsToDollars,
  states,
} from "../../../utils/utils";
import Cleave from "cleave.js/react";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useCurrentUser } from "../../../slices/auth/hooks";
import { DASHBOARD_USER_ROLES } from "../../../constants";
import CommissionsStructure from "../../../pages/Authentication/Profile/CommissionsStructure";
import { getCommissionsStructures } from "../../../slices/national/thunk";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

const AddRoleModal = ({ open, setOpen, roleToAdd, onCreate, loading,
  forUserId
}) => {
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  const isNational = currentUser?.role === DASHBOARD_USER_ROLES.NATIONAL;

  const allowCustomCommission = currentUser?.role === DASHBOARD_USER_ROLES.NATIONAL && !forUserId && !currentUser.hideCommissionStructure;

  const toggleModalAddUser = () => {
    setOpen(!open);
  };

  const [commissionsStructure, setCommissionsStructure] = useState({
    default: null,
    custom: [],
  });

  const [selectedCommission, setSelectedCommission] = useState();

  const selectedStructure =
    commissionsStructure?.custom?.find(
      (structure) => structure.id === selectedCommission
    ) || commissionsStructure?.default;

  const initialValuesBroker = {
    firstName: "",
    lastName: "",
    email: "",
    companyAddress: "",
    city: "",
    zipCode: "",
    state: "",
    phoneNumber: "",
    inheritPaymentInfo: false,
    customCommissionStructureId: "",
  };

  const initialValuesOther = {
    companyName: "",
    companyAddress: "",
    city: "",
    zipCode: "",
    state: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    customCommissionStructureId: "",
  };

  const commonValidations = {
    companyAddress: Yup.string().required("Address is required"),
    city: Yup.string().required("City/Town is required"),
    zipCode: Yup.string()
      .matches(/^[0-9]{1,5}$/, "Zip Code is not valid")
      .required("Zip Code is required"),
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone Number must be 10 digits")
      .required("Phone Number is required"),
    state: Yup.string().required("State is required"),
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
  };

  const validationSchemaBroker = Yup.object().shape(commonValidations);

  const validationSchemaOther = Yup.object().shape({
    ...commonValidations,
    companyName: Yup.string().required("Company Name is required"),
  });
  const onSubmit = async (values, { setSubmitting }) => {

    if (!allowCustomCommission) {
      delete values.customCommissionStructureId;
    }

    await onCreate(values);
    setSubmitting(false);
  };

  const [loadingCommissions, setLoadingCommissions] = useState(false);

  const fetchCommissionsStructures = async () => {
    try {
      setLoadingCommissions(true);
      const response = await dispatch(
        getCommissionsStructures({ id: currentUser.id })
      );
      setCommissionsStructure(response.payload);
      setLoadingCommissions(false);
    } catch (error) {
      console.log(error);
      setLoadingCommissions(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (open && allowCustomCommission) {
      fetchCommissionsStructures();
    }
  }, [open, currentUser.role]);

  useEffect(() => {
    if (!open || !allowCustomCommission) return;
    if (commissionsStructure?.current) {
      setSelectedCommission(commissionsStructure.current.id);
      initialValuesBroker.customCommissionStructureId =
        commissionsStructure.current.id;
      initialValuesOther.customCommissionStructureId =
        commissionsStructure.current.id;
    }
  }, [commissionsStructure]);

  const [loadingSelect, setLoadingSelect] = useState(null);

  const renderCommissionStructureBreakdown = (structure) => {

    // If it's a national adding a broker directly, remove GA commission and make national commission as national + ga.

    let nationalNumber = structure?.national;
    let gaNumber = structure?.ga;
    let brokerNumber = structure?.broker;

    if (roleToAdd === DASHBOARD_USER_ROLES.BROKER && currentUser.role === DASHBOARD_USER_ROLES.NATIONAL) {
      nationalNumber = nationalNumber + gaNumber;
      gaNumber = 0;
    }




    return (
      <Row>
        <Col>
          <p>
            National:{" "}
            <strong>
              ${formatCentsToDollars(nationalNumber)}
            </strong>
          </p>
          {gaNumber ? (
            <p>
              GA:{" "}
              <strong>
                ${formatCentsToDollars(gaNumber)}
              </strong>
            </p>
          ) : null}

          <p>
            Broker:{" "}
            <strong>
              ${formatCentsToDollars(brokerNumber)}
            </strong>
          </p>
        </Col>
      </Row>
    )

  }

  const renderCommissionStructures = (setFieldValue) => {
    return (
      <>
        <label htmlFor="commissionSelect" className="form-label mb-0">
          Commission Structure
        </label>
        {loadingCommissions ? (
          <div className="d-flex justify-content-center align-items-center mb-3">
            <Spinner color="primary" size="sm" />{" "}
          </div>
        ) : (
          <Col md={12}>
            {commissionsStructure?.current && (
              <Card className="p-3 border rounded-4 mt-3">
                <FormGroup>
                  {loadingSelect ? (
                    <div className="border py-2 rounded">
                      <Spinner size="sm" color="primary" className="ms-4" />
                    </div>
                  ) : (
                    <Input
                      type="select"
                      name="customCommissionStructureId"
                      id="commissionSelect"
                      value={
                        selectedCommission || commissionsStructure.current.id
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedCommission(value);
                        setFieldValue("customCommissionStructureId", value);
                      }}
                    >
                      <option value={commissionsStructure.current.id}>
                        Default Commission Structure
                      </option>

                      {commissionsStructure?.custom?.map((structure) => (
                        <option key={structure.id} value={structure.id}>
                          {structure.name}
                        </option>
                      ))}
                    </Input>
                  )}
                </FormGroup>
                <hr className="mt-0" />

                {renderCommissionStructureBreakdown(selectedStructure)}
                {loadingSelect === selectedStructure?.id && (
                  <Spinner size="sm" />
                )}
              </Card>
            )}
          </Col>
        )}
      </>
    );
  };


  return (
    <>
      <Modal isOpen={open} toggle={toggleModalAddUser}>
        <ModalHeader toggle={toggleModalAddUser}>
          Add {roleToAdd == "ga" ? "GA" : capitalizeFirstLetter(roleToAdd)}
        </ModalHeader>
        {loadingCommissions ? (
          <div className="d-flex justify-content-center align-items-center mb-5 mt-4">
            <Spinner
              color="primary"
              className="d-flex justify-content-center align-items-center"
            />
          </div>
        ) : (
          <Formik
            initialValues={
              roleToAdd == "broker" ? initialValuesBroker : initialValuesOther
            }
            validationSchema={
              roleToAdd == "broker"
                ? validationSchemaBroker
                : validationSchemaOther
            }
            onSubmit={onSubmit}
          >
            {({ isSubmitting, isValid, dirty, setFieldValue }) => {
              return (
                <ModalBody>
                  <Form className="ps-3">
                    {roleToAdd == "broker" ? (
                      <>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="firstName"
                                className="form-label mb-0"
                              >
                                First Name
                              </label>
                              <Field
                                type="text"
                                name="firstName"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="firstName"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="lastName"
                                className="form-label mb-0"
                              >
                                Last Name
                              </label>
                              <Field
                                type="text"
                                name="lastName"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="lastName"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <Col md={12}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="email"
                                className="form-label mb-0"
                              >
                                Email
                              </label>
                              <Field
                                type="email"
                                name="email"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="email"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="companyAddress"
                                className="form-label mb-0"
                              >
                                Address
                              </label>
                              <Field
                                type="text"
                                name="companyAddress"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="companyAddress"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label htmlFor="city" className="form-label mb-0">
                                City/Town
                              </label>
                              <Field
                                type="text"
                                name="city"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="city"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="col-12 d-flex justify-content-start align-items-center ">
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="state"
                                className="form-label mb-0"
                              >
                                State
                              </label>
                              <Field
                                as="select"
                                name="state"
                                className="form-control"
                              >
                                <option value="" disabled>
                                  Select State
                                </option>
                                {states.map((state, index) => (
                                  <>
                                    <option key={index} value={state}>
                                      {state}
                                    </option>
                                  </>
                                ))}
                              </Field>
                              <ErrorMessage
                                name="state"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="zipCode"
                                className="form-label mb-0"
                              >
                                Zip Code
                              </label>
                              <Field
                                onInput={(e) => {
                                  if (e.target.value.length > 5)
                                    e.target.value = e.target.value.slice(0, 5);
                                }}
                                type="number"
                                name="zipCode"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="zipCode"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Col md={6}>
                          <div className="mb-3 position-relative">
                            <label
                              htmlFor="phoneNumber"
                              className="form-label mb-0"
                            >
                              Phone Number
                            </label>
                            <Field name="phoneNumber">
                              {({ field, form, meta }) => (
                                <Cleave
                                  {...field}
                                  className="form-control"
                                  options={{
                                    numericOnly: true,
                                    delimiters: [, "(", ")-", "-"],
                                    blocks: [0, 0, 3, 3, 4],
                                    delimiterLazyShow: true,
                                  }}
                                  onChange={(e) => {
                                    form.setFieldValue(
                                      "phoneNumber",
                                      e.target.rawValue
                                    );
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="phoneNumber"
                              component="div"
                              className="text-danger position-absolute"
                              style={{ bottom: "-20px" }}
                            />
                          </div>
                        </Col>
                        {isNational && allowCustomCommission &&
                          renderCommissionStructures(setFieldValue)}
                      </>
                    ) : (
                      <>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <Col md={12}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="companyName"
                                className="form-label mb-0"
                              >
                                Company Name
                              </label>
                              <Field
                                type="text"
                                name="companyName"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="companyName"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="companyAddress"
                                className="form-label mb-0"
                              >
                                Address
                              </label>
                              <Field
                                type="text"
                                name="companyAddress"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="companyAddress"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label htmlFor="city" className="form-label mb-0">
                                City/Town
                              </label>
                              <Field
                                type="text"
                                name="city"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="city"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="state"
                                className="form-label mb-0"
                              >
                                State
                              </label>
                              <Field
                                as="select"
                                name="state"
                                className="form-control"
                              >
                                <option value="" disabled>
                                  Select State
                                </option>
                                {states.map((state, index) => (
                                  <>
                                    <option key={index} value={state}>
                                      {state}
                                    </option>
                                  </>
                                ))}
                              </Field>
                              <ErrorMessage
                                name="state"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="zipCode"
                                className="form-label mb-0"
                              >
                                Zip Code
                              </label>
                              <Field
                                onInput={(e) => {
                                  if (e.target.value.length > 5)
                                    e.target.value = e.target.value.slice(0, 5);
                                }}
                                type="number"
                                name="zipCode"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="zipCode"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="firstName"
                                className="form-label mb-0"
                              >
                                First Name
                              </label>
                              <Field
                                type="text"
                                name="firstName"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="firstName"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3 position-relative">
                              <label
                                htmlFor="lastName"
                                className="form-label mb-0"
                              >
                                Last Name
                              </label>
                              <Field
                                type="text"
                                name="lastName"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="lastName"
                                component="div"
                                className="text-danger position-absolute"
                                style={{ bottom: "-20px" }}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <div className="mb-3 position-relative">
                            <label htmlFor="email" className="form-label mb-0">
                              Email
                            </label>
                            <Field
                              type="email"
                              name="email"
                              className="form-control"
                            />
                            <ErrorMessage
                              name="email"
                              component="div"
                              className="text-danger position-absolute"
                              style={{ bottom: "-20px" }}
                            />
                          </div>
                        </Row>
                        <Row className="col-12 d-flex justify-content-center align-items-center">
                          <div className="mb-3 position-relative">
                            <label
                              htmlFor="phoneNumber"
                              className="form-label mb-0"
                            >
                              Phone Number
                            </label>
                            <Field name="phoneNumber">
                              {({ field, form, meta }) => (
                                <Cleave
                                  {...field}
                                  className="form-control"
                                  options={{
                                    numericOnly: true,
                                    delimiters: [, "(", ")-", "-"],
                                    blocks: [0, 0, 3, 3, 4],
                                    delimiterLazyShow: true,
                                  }}
                                  onChange={(e) => {
                                    form.setFieldValue(
                                      "phoneNumber",
                                      e.target.rawValue
                                    );
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="phoneNumber"
                              component="div"
                              className="text-danger position-absolute"
                              style={{ bottom: "-20px" }}
                            />
                          </div>
                        </Row>
                        {isNational &&
                          roleToAdd === "ga" && allowCustomCommission &&
                          renderCommissionStructures(setFieldValue)}
                      </>
                    )}
                    <Button
                      type="submit"
                      color="primary"
                      className="w-100"
                      disabled={isSubmitting || !isValid || !dirty || loading}
                    >
                      {isSubmitting ? (
                        <span className="spinner-border spinner-border-sm mr-3"></span>
                      ) : (
                        `Add ${capitalizeFirstLetter(roleToAdd)} `
                      )}
                    </Button>
                  </Form>
                </ModalBody>
              );
            }}
          </Formik>
        )}
      </Modal>
    </>
  );
};

export default AddRoleModal;
